import React, { useState, useContext, useEffect } from "react";
import { styled, Dialog, DialogContent, DialogTitle, DialogActions, Typography } from "@mui/material";
import { useTranslation, Trans } from "react-i18next";
import { StyledButton } from "../../../components/styled-button/styled-button";
import DeleteIcon from '@mui/icons-material/DeleteOutlineOutlined';
import { useService } from '../applications-service'; 
import { ApplicationsContext } from "../applications-context";
import pubSub, { EVENTS } from "../../../config/pubSub";
import { AppConfig } from "../../../context/context";

const DialogActionsContainer = styled(DialogActions)(({ theme }) => ({
  padding: theme.spacing(2),
  "& button": {
    margin: theme.spacing(1),
  },
}));

const StyledDialogTitle = styled(DialogTitle)(({ theme }) => ({
  paddingTop: theme.spacing(4),
  paddingLeft: theme.spacing(4),
  paddingRight: theme.spacing(4),
  paddingBottom: "0px",
  display: "flex"
}));

const StyledTypography = styled(Typography)(({ theme }) => ({
  color: "var(--kyocera-dark-grey)",
  fontFamily: "Noto Sans",
  fontSize: "36px",
  fontWeight: "bolder",
}));

const StyledDialogContent = styled(DialogContent)(({ theme }) => ({
  padding: theme.spacing(4),
  marginTop: "32px", 
}));

const DeleteAppContainer = styled('div')(({ theme }) => ({
  "& button.MuiButton-root": {
    "&.Mui-disabled": {
      opacity: 0.5,
      backgroundColor: "var(--kyocera-bg-grey)",
      boxShadow: "0px 3px 1px -2px rgba(0,0,0,0.2),0px 2px 2px 0px rgba(0,0,0,0.14),0px 1px 5px 0px rgba(0,0,0,0.12)",
      color: "var(--kyocera-text-grey)",
    },
    marginLeft: theme.spacing(2),
  },
}));

const DeleteApplicationComponent = () => {
  const [showDialog, setShowDialog] = useState(false);
  const { t } = useTranslation();
  const { appService } = useService();
  const { selectedCheckedApps, setSelectedApp, setSelectedCheckedApps, applications } = useContext(ApplicationsContext);
  const { config } = useContext(AppConfig);
  const [selectedAppNames, setSelectedAppNames] = useState<string[]>([]);

  useEffect(() => {
    if (selectedCheckedApps.length > 0 && applications?.devTenantApps) {
      const names = selectedCheckedApps.map(appId => {
        const app = applications.devTenantApps.find(app => app.id === appId);
        return app ? app.name : "";
      });
      setSelectedAppNames(names);
    } else {
      setSelectedAppNames([]);
    }
  }, [selectedCheckedApps, applications]);

  const handleCancel = () => {
    setShowDialog(false);
  };

  const handleClick = () => {
    setShowDialog(true);
  };

  const handleClickOk = async () => {
    try {
        // Send delete requests for selected organizations
        await Promise.all(selectedCheckedApps.map(appId => appService.deleteTenantApp(config.tenantId, appId)));

        // After successful deletion
        if (selectedCheckedApps.length === 1) {
          pubSub.publish(EVENTS.SUCCESS, t("common_delete_single_app_success_message"));
        } else {
          pubSub.publish(EVENTS.SUCCESS, t("common_delete_mult_app_success_message"));
        }
        setSelectedCheckedApps([]); // Clear multiple selection
        setSelectedApp(null); // Clear single selection
        pubSub.publish(EVENTS.ORGANIZATIONS_LIST); // Refresh list
    } catch (error) {
        console.error("Error deleting organizations:", error);
        // Handle error appropriately (e.g., show a notification)
    } finally {
        setShowDialog(false); // Close the dialog in the finally block
    }

};


  return (
    <>
      <DeleteAppContainer>
        <StyledButton
          variant="contained"
          onClick={handleClick}
          className="d-flex align-items-center"
          id="delete-organization-button"
          startIcon={<DeleteIcon />}
          disabled={selectedCheckedApps.length === 0}
        >
          {t("delete_application")}
        </StyledButton>
      </DeleteAppContainer>
      <Dialog
        open={showDialog}
        maxWidth="sm"
        fullWidth
        onClose={(event, reason) => {
          if (reason !== "backdropClick") {
            handleCancel();
          }
        }}
        disableEscapeKeyDown
      >
        <StyledDialogTitle id="form-dialog-title">
          <StyledTypography>{t("delete_application")}</StyledTypography>
        </StyledDialogTitle>
        <StyledDialogContent>
          {selectedCheckedApps.length === 1 ? (
            <Trans i18nKey="delete_application_msg" components={{ strong: <strong /> }} values={{ appName: selectedAppNames[0] }} />
          ) : (
              <Trans i18nKey="delete_application_msg_mult"  components={{ strong: <strong /> }} values={{ itemCount: selectedCheckedApps.length }} />
          )}
        </StyledDialogContent>
        <DialogActionsContainer>
          <StyledButton onClick={handleCancel} variant="contained">
            {t("common_cancel")}
          </StyledButton>
          <StyledButton
            onClick={handleClickOk}
            variant="contained"
            color="primary"
            id="button-delete-org-submit"
          >
            {t("delete")}
          </StyledButton>
        </DialogActionsContainer>
      </Dialog>
    </>
  );
};

export default DeleteApplicationComponent;
